/**
 * Session persistence for applied Patient Notification filters.
 */

import {
  normalizeNotificationFilters,
  type NotificationFilters,
} from "./notificationFilters";

const STORAGE_KEY = "ct.notifications.filters";

/**
 * Read and validate applied filters from the current browser-tab session.
 *
 * @returns Restored filters, or an empty object for missing/malformed state
 */
export function readStoredNotificationFilters(): NotificationFilters {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      clearStoredNotificationFilters();
      return {};
    }
    return normalizeNotificationFilters(parsed as NotificationFilters);
  } catch {
    clearStoredNotificationFilters();
    return {};
  }
}

/**
 * Persist applied filters for navigation and reloads in this browser tab.
 *
 * @param filters - Canonical filters to remember
 * @returns Nothing
 */
export function writeStoredNotificationFilters(
  filters: NotificationFilters,
): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(normalizeNotificationFilters(filters)),
    );
  } catch {
    // Storage can be unavailable in private mode; page state remains functional.
  }
}

/**
 * Clear persisted notification filters on Reset All or logout.
 *
 * @returns Nothing
 */
export function clearStoredNotificationFilters(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Missing storage requires no cleanup.
  }
}
